/**
 * App entry point — Vite 6 / React 19 / React Router v7.
 *
 * Provider stack (outermost → innermost):
 *   HelmetProvider  — react-helmet-async for <head> management
 *   ApolloProvider  — GraphQL client (JWT auth link injected)
 *   QueryClientProvider — TanStack Query for server-state
 *   I18nextProvider — react-i18next (initialized in ./i18n)
 *   RouterProvider  — React Router v7 browser router
 *
 * Side effects at import:
 *   ./sentry.client.config — installs Sentry before first render
 *   ./i18n               — initializes i18next (top-level await safe in Vite ESM)
 */

import './sentry.client.config'
import './i18n/index'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ApolloProvider } from '@apollo/client'
import { HelmetProvider } from 'react-helmet-async'
import { router } from './router'
import { queryClient } from './lib/query-client'
import { apolloClient } from './lib/apollo'
import './index.css'

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('[main] #root element not found')

createRoot(rootEl).render(
  <StrictMode>
    <HelmetProvider>
      <ApolloProvider client={apolloClient}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </ApolloProvider>
    </HelmetProvider>
  </StrictMode>,
)
